import React, {memo, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@emotion/react";

import {Loader} from "@pg-design/loader-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import type {IMarker, IMarkerEventCallback, IPolygon} from "@pg-mono/open-street-map";
import {convertToArrayOfLatLngLiterals, convertToCountryLatLngLiteral, LazyOpenStreetMap} from "@pg-mono/open-street-map";

import {osmPublicTileUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {IOSMPoisRoutesGrouped} from "../../../maps/actions/fetch_osm_pois_routes_grouped";
import {useOpenStreetMapAlgolyticsTracking} from "../../../maps/hooks/use_open_street_map_algolytics_tracking";
import {PoiType} from "../../../maps/types/PoiType";
import {isRailTransportAvailable} from "../../../maps/utils/is_rail_transport_available";
import {IRegionListMap} from "../../../region/types/IRegionListMap";
import {ViewType} from "../../../view_type/ViewType";
import {getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {useOfferListMapRailTransportElements} from "../../hooks/use_offer_list_map_rail_transport_elements";
import {getOfferMarkerIcon} from "../../list/components/OfferListMap/getOfferMarkerIcon";
import {MarkerPopup} from "../../list/components/OfferListMap/MarkerPopup";
import {Layer, setMapLayers} from "../../map/state/map_layers_slice";
import {IOfferListMapOffer} from "../../types/IOfferListMapOffer";
import {MapLayersBox} from "./MapLayersBox";

interface IProps {
    transportRoutes: IOSMPoisRoutesGrouped[] | null;
    viewType: ViewType;
    onMarkerClick: (offerId: number) => void;
    offers: IOfferListMapOffer[];
    regionData?: IRegionListMap[];
    favouriteOffers: number[];
    appliedOffers: number[];
    seenOfferIds?: number[];
}

export const OSMapForOffers = memo(function OSMapForOffers(props: IProps) {
    const showPublicTransportOnMap = useSelector((state: IRPStore) => state.maps.userPreferences.showPublicTransportOnMap);
    const dispatch = useDispatch();
    const {transportLines} = useSelector((state: IRPStore) => state.offerListMap.layers);
    const osmAlgolyticsTracking = useOpenStreetMapAlgolyticsTracking(props.viewType);

    const hasRailTransport = isRailTransportAvailable(props.transportRoutes);
    const mapElements = useOfferListMapRailTransportElements({shouldFetch: transportLines, routes: props.transportRoutes});

    useEffect(() => {
        if (props.transportRoutes === null && transportLines) {
            dispatch(setMapLayers({[Layer.TransportLines]: false}));
        }

        if (props.transportRoutes && !transportLines && showPublicTransportOnMap) {
            dispatch(setMapLayers({[Layer.TransportLines]: true}));
        }
    }, [props.transportRoutes, showPublicTransportOnMap]);

    const onMarkerClick: IMarkerEventCallback = (params) => {
        if (params.marker.poiType !== PoiType.TRANSPORT) {
            props.onMarkerClick(params.marker.id as number);

            setTimeout(() => {
                params.map?.invalidateSize({animate: true});
            }, 300);
        }
    };

    const hasAbroadOffers = props.offers.some((offer) => offer.region.country !== 1);

    const markers = [
        ...props.offers.map((offer) => getOfferMarker(offer, props.favouriteOffers, props.appliedOffers, props.seenOfferIds)),
        ...(mapElements && mapElements.markers && transportLines ? mapElements.markers : [])
    ];

    const polygons: IPolygon[] | undefined = props.regionData?.map((region) => ({
        id: region.short_name,
        positions: convertToArrayOfLatLngLiterals(region.area.geo_area.coordinates, {reversedValues: true}),
        color: "#ff0000",
        opacity: 0.5,
        fillOpacity: 0.02
    }));

    const polyLines = mapElements && mapElements.polylines && transportLines ? mapElements.polylines : undefined;

    return (
        <>
            <React.Suspense
                fallback={
                    <div css={loaderHolder}>
                        <Loader size="lg" />
                    </div>
                }
            >
                <LazyOpenStreetMap
                    {...osmAlgolyticsTracking}
                    markers={markers}
                    polygons={polygons}
                    onMarkerClick={onMarkerClick}
                    polylines={polyLines}
                    clusterMarkers={{clusterWebOnMaxZoom: false, clusterWithAmountBadge: false}}
                    tileUrl={hasAbroadOffers ? osmPublicTileUrl : null}
                    onMarkerInvalidCoords={(marker) => {
                        const message = "Invalid marker coords in OSMapForOffers";

                        notifyBugsnagClient(new Error(message), message, JSON.stringify(marker));
                    }}
                />
                {hasRailTransport && <MapLayersBox transportRoutes={props.transportRoutes} />}
            </React.Suspense>
        </>
    );
});

//  Utils
function getOfferMarker(offer: IOfferListMapOffer, favouriteOffers: number[], appliedOffers: number[], seenOfferIds?: number[]): IMarker {
    const {vendor, id, geo_point, type, stats, name} = offer;
    const isFavourite = favouriteOffers.includes(id);
    const hasBeenApplicationSent = appliedOffers.includes(id);
    const hasBeenSeen = seenOfferIds ? seenOfferIds.includes(id) : false;

    return {
        id,
        coords: convertToCountryLatLngLiteral(geo_point.coordinates, offer.region.country),
        popup: () => (
            <MarkerPopup title={name} subtitle={vendor?.name} text={`liczba ${getOfferTypeNamePluralWhat(type)}: ${stats?.properties_count_for_sale ?? 0}`} />
        ),
        icon: {
            url: getOfferMarkerIcon(isFavourite, !!hasBeenApplicationSent, hasBeenSeen),
            sizes: [28, 28]
        },
        poiType: PoiType.OFFERS
    };
}

//  Styles
const loaderHolder = css`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
`;
import React, {memo, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@emotion/react";

import {Loader} from "@pg-design/loader-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import type {IMarker, IMarkerEventCallback, IPolygon} from "@pg-mono/open-street-map";
import {convertToArrayOfLatLngLiterals, convertToCountryLatLngLiteral, LazyOpenStreetMap} from "@pg-mono/open-street-map";

import {osmPublicTileUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {IOSMPoisRoutesGrouped} from "../../../maps/actions/fetch_osm_pois_routes_grouped";
import {useOpenStreetMapAlgolyticsTracking} from "../../../maps/hooks/use_open_street_map_algolytics_tracking";
import {PoiType} from "../../../maps/types/PoiType";
import {isRailTransportAvailable} from "../../../maps/utils/is_rail_transport_available";
import {IRegionListMap} from "../../../region/types/IRegionListMap";
import {ViewType} from "../../../view_type/ViewType";
import {getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {useOfferListMapRailTransportElements} from "../../hooks/use_offer_list_map_rail_transport_elements";
import {getOfferMarkerIcon} from "../../list/components/OfferListMap/getOfferMarkerIcon";
import {MarkerPopup} from "../../list/components/OfferListMap/MarkerPopup";
import {Layer, setMapLayers} from "../../map/state/map_layers_slice";
import {IOfferListMapOffer} from "../../types/IOfferListMapOffer";
import {MapLayersBox} from "./MapLayersBox";

interface IProps {
    transportRoutes: IOSMPoisRoutesGrouped[] | null;
    viewType: ViewType;
    onMarkerClick: (offerId: number) => void;
    offers: IOfferListMapOffer[];
    regionData?: IRegionListMap[];
    favouriteOffers: number[];
    appliedOffers: number[];
    seenOfferIds?: number[];
}

export const OSMapForOffers = memo(function OSMapForOffers(props: IProps) {
    const showPublicTransportOnMap = useSelector((state: IRPStore) => state.maps.userPreferences.showPublicTransportOnMap);
    const dispatch = useDispatch();
    const {transportLines} = useSelector((state: IRPStore) => state.offerListMap.layers);
    const osmAlgolyticsTracking = useOpenStreetMapAlgolyticsTracking(props.viewType);

    const hasRailTransport = isRailTransportAvailable(props.transportRoutes);
    const mapElements = useOfferListMapRailTransportElements({shouldFetch: transportLines, routes: props.transportRoutes});

    useEffect(() => {
        if (props.transportRoutes === null && transportLines) {
            dispatch(setMapLayers({[Layer.TransportLines]: false}));
        }

        if (props.transportRoutes && !transportLines && showPublicTransportOnMap) {
            dispatch(setMapLayers({[Layer.TransportLines]: true}));
        }
    }, [props.transportRoutes, showPublicTransportOnMap]);

    const onMarkerClick: IMarkerEventCallback = (params) => {
        if (params.marker.poiType !== PoiType.TRANSPORT) {
            props.onMarkerClick(params.marker.id as number);

            setTimeout(() => {
                params.map?.invalidateSize({animate: true});
            }, 300);
        }
    };

    const hasAbroadOffers = props.offers.some((offer) => offer.region.country !== 1);

    const markers = [
        ...props.offers.map((offer) => getOfferMarker(offer, props.favouriteOffers, props.appliedOffers, props.seenOfferIds)),
        ...(mapElements && mapElements.markers && transportLines ? mapElements.markers : [])
    ];

    const polygons: IPolygon[] | undefined = props.regionData?.map((region) => ({
        id: region.short_name,
        positions: convertToArrayOfLatLngLiterals(region.area.geo_area.coordinates, {reversedValues: true}),
        color: "#ff0000",
        opacity: 0.5,
        fillOpacity: 0.02
    }));

    const polyLines = mapElements && mapElements.polylines && transportLines ? mapElements.polylines : undefined;

    return (
        <>
            <React.Suspense
                fallback={
                    <div css={loaderHolder}>
                        <Loader size="lg" />
                    </div>
                }
            >
                <LazyOpenStreetMap
                    {...osmAlgolyticsTracking}
                    markers={markers}
                    polygons={polygons}
                    onMarkerClick={onMarkerClick}
                    polylines={polyLines}
                    clusterMarkers={{clusterWebOnMaxZoom: false, clusterWithAmountBadge: false}}
                    tileUrl={hasAbroadOffers ? osmPublicTileUrl : null}
                    onMarkerInvalidCoords={(marker) => {
                        const message = "Invalid marker coords in OSMapForOffers";

                        notifyBugsnagClient(new Error(message), message, JSON.stringify(marker));
                    }}
                />
                {hasRailTransport && <MapLayersBox transportRoutes={props.transportRoutes} />}
            </React.Suspense>
        </>
    );
});

//  Utils
function getOfferMarker(offer: IOfferListMapOffer, favouriteOffers: number[], appliedOffers: number[], seenOfferIds?: number[]): IMarker {
    const {vendor, id, geo_point, type, stats, name} = offer;
    const isFavourite = favouriteOffers.includes(id);
    const hasBeenApplicationSent = appliedOffers.includes(id);
    const hasBeenSeen = seenOfferIds ? seenOfferIds.includes(id) : false;

    return {
        id,
        coords: convertToCountryLatLngLiteral(geo_point.coordinates, offer.region.country),
        popup: () => (
            <MarkerPopup title={name} subtitle={vendor?.name} text={`liczba ${getOfferTypeNamePluralWhat(type)}: ${stats?.properties_count_for_sale ?? 0}`} />
        ),
        icon: {
            url: getOfferMarkerIcon(isFavourite, !!hasBeenApplicationSent, hasBeenSeen),
            sizes: [28, 28]
        },
        poiType: PoiType.OFFERS
    };
}

//  Styles
const loaderHolder = css`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
`;
